import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import { add_school_student_api } from "../DAL/SchoolPortal/SchoolPortal";
import { get_instructor_join_link_api } from "../DAL/Instructors/Instructors";
import { useSchool } from "../Hooks/SchoolContext";
import {
  border, textPrimary, textMuted, font,
  inputSx, primaryButtonSx,
} from "../layouts/schoolPortal/schoolPortalTheme";

/**
 * The two ways an instructor gets students in: their permanent join link, and
 * a one-off email invite. Extracted from the onboarding checklist so the same
 * component serves both onboarding and the everyday Students page — the link
 * is needed long after setup is finished.
 *
 * The link is derived from the instructor's name (or, for a school, the school's
 * name) and never changes, so there is no regenerate control: it is meant to be
 * pasted in a bio and printed on a handout.
 *
 * A school's link is billed as a seat, like an email invite, so nothing here
 * promises free days to a school's students.
 */
export default function InstructorInviteTools({ onStudentAdded }) {
  const { enqueueSnackbar } = useSnackbar();
  const { isIndependentInstructor, school } = useSchool();
  const [joinLink, setJoinLink] = useState(null);
  const [invite, setInvite] = useState({ fullName: "", email: "" });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    get_instructor_join_link_api()
      .then((r) => { if (!cancelled && r && r.url) setJoinLink(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!joinLink?.url) return;
    try {
      await navigator.clipboard.writeText(joinLink.url);
      enqueueSnackbar("Link copied", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Could not copy. Select the link and copy it by hand.", { variant: "warning" });
    }
  };

  const handleWhatsApp = () => {
    if (!joinLink?.url) return;
    const text = isIndependentInstructor
      ? `I've set up a PPL group on XAir. Join with this link for 14 days free: ${joinLink.url}`
      : `Join ${school?.name || "our school"} on XAir to study for your PPL theory: ${joinLink.url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = invite.email.trim().toLowerCase();
    // One field on screen, two on the wire. Everything before the first space
    // is the first name, everything after it is the surname — so "Anne Marie
    // de la Cruz" keeps its whole surname rather than losing the tail.
    // add_student.js rejects a missing lastName, hence the check.
    const parts = invite.fullName.trim().split(/\s+/).filter(Boolean);
    if (!email || parts.length < 2) {
      enqueueSnackbar(
        parts.length === 1 ? "Add their surname too" : "Give their full name and email",
        { variant: "error" }
      );
      return;
    }
    setInviting(true);
    try {
      const r = await add_school_student_api({
        firstName: parts[0],
        lastName: parts.slice(1).join(" "),
        email,
      });
      if (r.code === 200 || r.code === 201) {
        enqueueSnackbar(`Invite sent to ${email}`, { variant: "success" });
        setInvite({ fullName: "", email: "" });
        if (onStudentAdded) onStudentAdded();
      } else {
        enqueueSnackbar(r.message || "Could not invite that student", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Could not invite that student", { variant: "error" });
    } finally {
      setInviting(false);
    }
  };

  const smallPrimarySx = { ...primaryButtonSx, minHeight: 34, padding: "6px 16px", fontSize: "12.5px", whiteSpace: "nowrap" };

  return (
    <Box>
      {/* Link and its actions on one line. The URL truncates rather than
          wrapping — nobody reads it, they copy it — and "permanent" in the
          label carries what a sentence of reassurance used to. */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        p: 0.75, pl: 1.5, mb: 1.75,
        borderRadius: "10px", backgroundColor: "#FAF8F5", border: `1px solid ${border}`,
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: textMuted, lineHeight: 1.4 }}>
            {isIndependentInstructor ? "Your permanent link" : "Your school's permanent link"}
          </Typography>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary, userSelect: "all" }}>
            {joinLink?.url || (joinLink === null ? "Loading" : "Not available")}
          </Typography>
        </Box>
        {joinLink?.url && (
          <>
            <Tooltip title="Share on WhatsApp" arrow>
              <IconButton onClick={handleWhatsApp} size="small" aria-label="Share on WhatsApp" sx={{ flexShrink: 0 }}>
                <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366" }} />
              </IconButton>
            </Tooltip>
            <Button onClick={handleCopy} startIcon={<ContentCopyIcon sx={{ fontSize: "15px !important" }} />} sx={{ ...smallPrimarySx, flexShrink: 0 }}>
              Copy
            </Button>
          </>
        )}
      </Box>

      {/* One row: name, email, send. Anything else is a field an instructor has
          to think about before they can invite someone. */}
      <Box component="form" onSubmit={handleInvite} sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" },
        gap: 1,
        alignItems: "center",
      }}>
        <TextField
          label="Full name" size="small" required value={invite.fullName}
          onChange={(e) => setInvite({ ...invite, fullName: e.target.value })}
          sx={inputSx}
        />
        <TextField
          label="Email" type="email" size="small" required value={invite.email}
          onChange={(e) => setInvite({ ...invite, email: e.target.value })}
          sx={inputSx}
        />
        <Button type="submit" disabled={inviting} sx={{ ...smallPrimarySx, minHeight: 40 }}>
          {inviting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Send"}
        </Button>
      </Box>
    </Box>
  );
}
